import { createAsyncThunk } from "@reduxjs/toolkit";

import { services } from "../../services";
import {
	LitheStepEnum,
	type TLitheCreationData,
} from "../../types/lithe.type";
import {
	normalizeLitheStep,
	normalizeLitheSteps,
} from "../../utils/litheUtils";

const buildThunkErrorPayload = (
	error: any,
	fallbackMessage: string
) => {
	const responseData = error?.response?.data;
	const errorMessage =
		responseData?.msg ??
		responseData?.error_msg ??
		error?.message ??
		fallbackMessage;

	return {
		...(responseData ?? {}),
		msg: `Error occurred: ${errorMessage}`,
	};
};

export const createLitheThunk = createAsyncThunk(
	"lithe/createLitheThunk",
	async (
		litheCreationData: TLitheCreationData,
		{ dispatch, rejectWithValue }
	) => {
		try {
			const data = await services.litheService.createLithe(
				litheCreationData
			);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to create lithe."
				)
			);
		}
	}
);

export const fetchLitheThunk = createAsyncThunk(
	"lithe/fetchLitheThunk",
	async (litheId: string, { rejectWithValue }) => {
		try {
			const metaData =
				await services.litheService.getLithe(litheId);

			const stepsData = await services.litheService
				.getLitheSteps(litheId)
				.catch(() => null);
			const rawAllSteps = (
				Array.isArray(stepsData?.all_steps) &&
				stepsData.all_steps.length > 0
					? stepsData.all_steps
					: metaData?.all_steps
			) as Array<string> | null | undefined;
			const rawCurrentStep = (stepsData?.current_step ??
				metaData?.current_step) as
				| string
				| null
				| undefined;
			const fallbackCurrentStep = metaData?.current_step as
				| string
				| null
				| undefined;

			return {
				...metaData,
				quick_start:
					stepsData?.quick_start ??
					metaData?.quick_start ??
					{},
				lithe_type:
					stepsData?.lithe_type ?? metaData?.lithe_type,
				all_steps: normalizeLitheSteps(rawAllSteps),
				current_step:
					normalizeLitheStep(rawCurrentStep) ??
					normalizeLitheStep(fallbackCurrentStep) ??
					LitheStepEnum.SPECS,
			};
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load lithe metadata."
				)
			);
		}
	}
);

export const fetchLitheSpecsQuestionsThunk =
	createAsyncThunk(
		"lithe/fetchLitheSpecsQuestionsThunk",
		async (litheId: string, { rejectWithValue }) => {
			try {
				const data =
					await services.litheService.getLitheSpecsQuestions(
						litheId
					);
				return data;
			} catch (error: any) {
				console.error("redux error", error);
				return rejectWithValue(
					buildThunkErrorPayload(
						error,
						"Unable to load the required fields."
					)
				);
			}
		}
	);

export const fetchLitheApprovalInfo = createAsyncThunk(
	"lithe/fetchLitheApprovalInfo",
	async (
		{
			litheId,
			isUserGuided = false,
		}: { litheId: string; isUserGuided?: boolean },
		{ rejectWithValue }
	) => {
		try {
			const data =
				await services.litheService.getLitheApprovalInfo(
					litheId,
					isUserGuided
				);
			return data;
		} catch (error: any) {
			console.error("redux error", error);
			return rejectWithValue(
				buildThunkErrorPayload(
					error,
					"Unable to load approval info."
				)
			);
		}
	}
);
